import { useEffect, useRef, useState } from 'react'
import {
  Plus, Save, Trash2, Search, Eye, CheckCircle2, AlertTriangle, Sparkles, Globe,
  Image as ImageIcon, Upload, Loader2,
} from 'lucide-react'
import { PageHead } from '../AdminLayout'
import {
  Panel, DataTable, RefreshButton, ConfirmButton, Drawer, Toolbar, Select, SearchBox,
  Pager, Tag,
} from '../ui'
import Editor from '../Editor'
import MediaPicker, { uploadImage } from '../MediaPicker'
import { get, post, patch, del, qs } from '../../lib/api'
import { toast } from '../../store'
import { fa, faDate, asArray } from '../../lib/format'

const EMPTY = {
  title: '', slug: '', excerpt: '', body: '', category: 'اخبار', tags: '',
  cover_url: '', cover_alt: '', author: '', status: 'draft', featured: false,
  published_at: '', meta_title: '', meta_description: '', keywords: '',
  canonical: '', noindex: false, faq: '[]',
}

const STATUS_LABEL: Record<string, string> = {
  published: 'منتشرشده', draft: 'پیش‌نویس', scheduled: 'زمان‌بندی‌شده',
}

/** تبدیل خروجی API (camelCase) به شکل ورودی فرم (snake_case) */
function toForm(r: any) {
  return {
    id: r.id,
    title: r.title || '', slug: r.slug || '', excerpt: r.excerpt || '', body: r.body || '',
    category: r.category || 'اخبار', tags: r.tags || '',
    cover_url: r.coverUrl || '', cover_alt: r.coverAlt || '', author: r.author || '',
    status: r.status || 'draft', featured: !!r.featured,
    published_at: r.publishedAt ? String(r.publishedAt).slice(0, 16) : '',
    meta_title: r.metaTitle || '', meta_description: r.metaDescription || '',
    keywords: r.keywords || '', canonical: r.canonical || '', noindex: !!r.noindex,
    faq: r.faq || '[]',
  }
}

/* --------------------------------------------------- پیش‌نمایش نتیجه گوگل */
function Serp({ seo, slug }: { seo: any; slug: string }) {
  // رنگ‌ها عمداً همان رنگ‌های واقعی گوگل‌اند تا نویسنده دقیقاً ببیند نتیجه‌اش
  // در صفحه جستجو چه شکلی است — نه یک نسخه تیره و خوش‌ظاهر.
  const url = seo?.canonical || `/blog/${slug}`
  return (
    <div className="rounded-2xl border border-white/[.07] bg-white p-4">
      <div className="mb-1 truncate text-[12px] text-[#202124]" dir="ltr">{url}</div>
      <div className="mb-1 text-[18px] leading-7 text-[#1a0dab]">{seo?.title || '—'}</div>
      <div className="text-[13px] leading-6 text-[#4d5156]">{seo?.description || '—'}</div>
    </div>
  )
}

/* --------------------------------------------------- بررسی سئو */
function Audit({ audit }: { audit: any }) {
  if (!audit) return null
  const checks = asArray<any>(audit.checks)
  const color = audit.score >= 80 ? 'text-up' : audit.score >= 50 ? 'text-gold' : 'text-down'
  return (
    <div className="rounded-2xl border border-white/[.07] bg-ink-950/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs font-bold text-slate-200">
          <Sparkles className="h-4 w-4 text-brand-300" /> بررسی سئو
        </span>
        <span className={`num text-lg font-black ${color}`}>{fa(audit.score)}٪</span>
      </div>
      <div className="mb-3 flex flex-wrap gap-2 text-[11px] text-slate-500">
        <span>{fa(audit.words)} کلمه</span>
        <span>·</span>
        <span>{fa(audit.headings)} تیتر</span>
        <span>·</span>
        <span>{fa(audit.internalLinks)} لینک داخلی</span>
        <span>·</span>
        <span>{fa(audit.externalLinks)} لینک بیرونی</span>
        {audit.focusKeyword ? (<><span>·</span><span>کلیدواژه: {audit.focusKeyword}</span></>) : null}
      </div>
      <ul className="space-y-1.5">
        {checks.map((c: any) => (
          <li key={c.key} className="flex items-start gap-2 text-[11px] leading-6">
            {c.ok
              ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-up" />
              : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />}
            <span className={c.ok ? 'text-slate-500' : 'text-slate-300'}>
              <b className="font-semibold">{c.label}</b>
              {c.ok ? null : <> — {c.hint}</>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* --------------------------------------------------- ویرایشگر پرسش‌وپاسخ */
function FaqEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  let rows: any[] = []
  try {
    const parsed = JSON.parse(value || '[]')
    rows = Array.isArray(parsed) ? parsed : []
  } catch {
    rows = []
  }
  const write = (next: any[]) => onChange(JSON.stringify(next))

  return (
    <div className="space-y-2">
      {rows.map((r: any, i: number) => (
        <div key={i} className="rounded-xl border border-white/[.07] p-3">
          <input
            className="input mb-2 !py-2 text-xs"
            placeholder="پرسش"
            value={r.q || ''}
            onChange={(e) => write(rows.map((x, n) => (n === i ? { ...x, q: e.target.value } : x)))}
          />
          <textarea
            className="input mb-2 min-h-[60px] !py-2 text-xs"
            placeholder="پاسخ"
            value={r.a || ''}
            onChange={(e) => write(rows.map((x, n) => (n === i ? { ...x, a: e.target.value } : x)))}
          />
          <button className="btn-ghost !py-1.5 text-[11px]" onClick={() => write(rows.filter((_, n) => n !== i))}>
            <Trash2 className="h-3 w-3" /> حذف
          </button>
        </div>
      ))}
      <button className="btn-ghost !py-2 text-xs" onClick={() => write([...rows, { q: '', a: '' }])}>
        <Plus className="h-3.5 w-3.5" /> افزودن پرسش
      </button>
      <p className="text-[11px] leading-6 text-slate-500">
        دو پرسش و پاسخ یا بیشتر، شانس نمایش «نتیجه غنی» در گوگل را می‌سازد.
      </p>
    </div>
  )
}

/* --------------------------------------------------- تصویر شاخص */
function CoverField({ url, alt, onUrl, onAlt }: {
  url: string; alt: string; onUrl: (v: string) => void; onAlt: (v: string) => void
}) {
  const [picker, setPicker] = useState(false)
  const [busy, setBusy] = useState(false)
  const [drag, setDrag] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const upload = async (files: FileList | null) => {
    const f = Array.from(files || []).find((x) => x.type.startsWith('image/'))
    if (!f) return
    setBusy(true)
    try {
      const r = await uploadImage(f)
      onUrl(r.url)
      if (!alt && r.alt) onAlt(r.alt)
      toast.ok('تصویر شاخص تنظیم شد')
    } catch (e: any) {
      toast.err(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mb-3">
      <div
        className={`overflow-hidden rounded-2xl border border-dashed transition ${
          drag ? 'border-brand-500/60 bg-brand-500/[.06]' : 'border-white/[.12] bg-white/[.02]'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDrag(true) }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files) }}
      >
        {url ? (
          <div className="relative">
            <img src={url} alt={alt || 'تصویر شاخص'}
                 className="h-44 w-full object-cover"
                 onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.25' }} />
            <div className="absolute inset-x-0 bottom-0 flex flex-wrap gap-2 bg-gradient-to-t from-black/80 to-transparent p-3">
              <button type="button" className="btn-ghost !py-1.5 text-[11px]"
                      onClick={() => fileRef.current?.click()}>
                <Upload className="h-3.5 w-3.5" /> جایگزینی
              </button>
              <button type="button" className="btn-ghost !py-1.5 text-[11px]" onClick={() => setPicker(true)}>
                <ImageIcon className="h-3.5 w-3.5" /> از کتابخانه
              </button>
              <button type="button" className="btn-ghost !py-1.5 text-[11px]" onClick={() => onUrl('')}>
                <Trash2 className="h-3.5 w-3.5" /> برداشتن
              </button>
            </div>
          </div>
        ) : (
          <div className="grid place-items-center gap-2 py-8 text-center">
            {busy
              ? <Loader2 className="h-6 w-6 animate-spin text-brand-300" />
              : <ImageIcon className="h-6 w-6 text-slate-600" />}
            <p className="text-xs text-slate-400">تصویر را اینجا رها کنید</p>
            <div className="flex gap-2">
              <button type="button" className="btn-primary !py-1.5 text-[11px]" disabled={busy}
                      onClick={() => fileRef.current?.click()}>
                <Upload className="h-3.5 w-3.5" /> انتخاب فایل
              </button>
              <button type="button" className="btn-ghost !py-1.5 text-[11px]" onClick={() => setPicker(true)}>
                از کتابخانه
              </button>
            </div>
            <p className="text-[10px] text-slate-600">JPG، PNG، WebP یا AVIF — تا ۵ مگابایت</p>
          </div>
        )}
      </div>

      <input ref={fileRef} type="file" hidden
             accept="image/png,image/jpeg,image/gif,image/webp,image/avif"
             onChange={(e) => { upload(e.target.files); e.target.value = '' }} />

      {/* متن جایگزین: برای سئوی جستجوی تصویر و برای کاربران نابینا لازم است */}
      <input className="input mt-2 text-xs" value={alt}
             placeholder="متن جایگزین تصویر (alt) — مثلاً: نمودار قیمت بیت‌کوین در سال ۱۴۰۵"
             onChange={(e) => onAlt(e.target.value)} />

      {/* نشانی دستی، برای وقتی تصویر روی سرور دیگری میزبانی می‌شود */}
      <input className="input num mt-2 text-[11px]" dir="ltr" value={url}
             placeholder="یا نشانی تصویر را مستقیم بگذارید"
             onChange={(e) => onUrl(e.target.value)} />

      <MediaPicker open={picker} onClose={() => setPicker(false)}
                   onPick={(u, a) => { onUrl(u); if (!alt && a) onAlt(a); setPicker(false) }} />
    </div>
  )
}

const counterClass = (len: number, min: number, max: number) =>
  len >= min && len <= max ? 'text-up' : 'text-gold'

/* --------------------------------------------------- صفحه */
export default function Content() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [counts, setCounts] = useState<any>({})
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [edit, setEdit] = useState<any>(null)
  const [preview, setPreview] = useState<any>(null)
  const [tab, setTab] = useState<'content' | 'seo' | 'faq'>('content')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/content/posts${qs({ status, q, page, per_page: 25 })}`)
      .then((r: any) => { setRows(asArray(r, 'data')); setMeta(r.meta); setCounts(r.counts || {}) })
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }
  useEffect(load, [status, q, page])

  // پیش‌نمایش سئو با تأخیر نیم‌ثانیه‌ای، تا هر کلید فشرده یک درخواست نزند
  useEffect(() => {
    if (!edit) { setPreview(null); return }
    const timer = setTimeout(() => {
      post('/admin/content/posts/preview-seo', edit).then(setPreview).catch(() => setPreview(null))
    }, 500)
    return () => clearTimeout(timer)
  }, [edit?.title, edit?.slug, edit?.excerpt, edit?.body, edit?.meta_title,
      edit?.meta_description, edit?.keywords, edit?.tags, edit?.cover_url,
      edit?.cover_alt, edit?.canonical, edit?.faq, edit?.status])

  const open = async (r: any) => {
    setTab('content')
    try {
      const full = await get(`/admin/content/posts/${r.id}`)
      setEdit(toForm(full))
    } catch {
      setEdit(toForm(r))
    }
  }

  const save = async () => {
    setSaving(true)
    try {
      if (edit.id) await patch(`/admin/content/posts/${edit.id}`, edit)
      else await post('/admin/content/posts', edit)
      toast.ok('ذخیره شد')
      setEdit(null)
      load()
    } catch (e: any) {
      toast.err(e.message)
    } finally {
      setSaving(false)
    }
  }

  const set = (values: any) => setEdit((e: any) => ({ ...e, ...values }))

  const titleLen = (edit?.meta_title || edit?.title || '').length
  const descLen = (edit?.meta_description || edit?.excerpt || '').length

  return (
    <>
      <PageHead
        title="مدیریت وبلاگ"
        subtitle="انتشار حرفه‌ای مطالب همراه با ابزار سئو"
        action={
          <div className="flex gap-2">
            <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="btn-ghost !py-2 text-xs">
              <Globe className="h-3.5 w-3.5" /> نقشه سایت
            </a>
            <RefreshButton onClick={load} loading={loading} />
            <button onClick={() => { setEdit({ ...EMPTY }); setTab('content') }} className="btn-primary !py-2 text-xs">
              <Plus className="h-3.5 w-3.5" /> مطلب جدید
            </button>
          </div>
        }
      />

      <Panel dense>
        <div className="p-4 pb-0">
          <Toolbar>
            <Select
              value={status}
              onChange={(v) => { setStatus(v); setPage(1) }}
              options={[
                ['', 'همه'],
                ['published', `منتشرشده (${fa(counts.published || 0)})`],
                ['draft', `پیش‌نویس (${fa(counts.draft || 0)})`],
                ['scheduled', `زمان‌بندی‌شده (${fa(counts.scheduled || 0)})`],
              ]}
            />
            <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1) }} placeholder="جستجوی عنوان…" />
          </Toolbar>
        </div>

        <DataTable
          rows={rows} loading={loading} minWidth={880} empty="مطلبی ثبت نشده است"
          onRowClick={open}
          columns={[
            { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
            {
              key: 't', header: 'عنوان',
              cell: (r: any) => (
                <div className="min-w-0">
                  <div className="truncate text-slate-100">{r.title}</div>
                  <div className="num truncate text-[11px] text-slate-600" dir="ltr">/blog/{r.slug}</div>
                </div>
              ),
            },
            { key: 'c', header: 'دسته', cell: (r: any) => <span className="text-slate-400">{r.category}</span> },
            {
              key: 'st', header: 'وضعیت',
              cell: (r: any) => (
                <Tag kind={r.status === 'published' ? 'up' : r.status === 'draft' ? 'muted' : 'gold'}>
                  {STATUS_LABEL[r.status] || r.status}
                </Tag>
              ),
            },
            {
              key: 'seo', header: 'سئو',
              cell: (r: any) => {
                const s = r.audit?.score ?? 0
                return (
                  <span className={`num text-xs font-bold ${s >= 80 ? 'text-up' : s >= 50 ? 'text-gold' : 'text-down'}`}>
                    {fa(s)}٪
                  </span>
                )
              },
            },
            { key: 'v', header: 'بازدید', cell: (r: any) => <span className="num text-slate-500">{fa(r.views || 0)}</span> },
            {
              key: 'd', header: 'تاریخ',
              cell: (r: any) => <span className="text-slate-500">{faDate(r.publishedAt || r.createdAt, false)}</span>,
            },
            {
              key: 'x', header: '',
              cell: (r: any) => (
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <a href={`/blog/${r.slug}`} target="_blank" rel="noreferrer"
                     className="btn-ghost !px-3 !py-1.5 text-xs" title="مشاهده در سایت">
                    <Eye className="h-3.5 w-3.5" />
                  </a>
                  <ConfirmButton
                    variant="ghost" label="حذف" question="حذف شود؟"
                    icon={<Trash2 className="h-3.5 w-3.5" />}
                    onConfirm={async () => {
                      try { await del(`/admin/content/posts/${r.id}`); toast.ok('حذف شد'); load() }
                      catch (e: any) { toast.err(e.message) }
                    }}
                  />
                </div>
              ),
            },
          ]}
        />
        <Pager meta={meta} onPage={setPage} />
      </Panel>

      <Drawer open={!!edit} onClose={() => setEdit(null)} wide
              title={edit?.id ? 'ویرایش مطلب' : 'مطلب جدید'}>
        {edit && (
          <>
            <div className="mb-4 flex gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
              {[['content', 'محتوا'], ['seo', 'سئو'], ['faq', 'پرسش‌های متداول']].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setTab(k as any)}
                  className={`flex-1 rounded-lg px-3 py-2 text-xs transition ${
                    tab === k
                      ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === 'content' && (
              <>
                <label className="label">عنوان</label>
                <input className="input mb-3" value={edit.title}
                       onChange={(e) => set({ title: e.target.value })} />

                <label className="label">نشانی صفحه (خالی بگذارید تا از عنوان ساخته شود)</label>
                <input className="input num mb-3" dir="ltr" value={edit.slug}
                       placeholder="به‌صورت خودکار ساخته می‌شود"
                       onChange={(e) => set({ slug: e.target.value })} />

                <div className="mb-3 grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="label">دسته‌بندی</label>
                    <input className="input" value={edit.category}
                           onChange={(e) => set({ category: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">نویسنده</label>
                    <input className="input" value={edit.author} placeholder="تیم ارزینو"
                           onChange={(e) => set({ author: e.target.value })} />
                  </div>
                </div>

                <label className="label">برچسب‌ها (با کاما جدا کنید)</label>
                <input className="input mb-3" value={edit.tags} placeholder="بیت کوین، کیف پول، امنیت"
                       onChange={(e) => set({ tags: e.target.value })} />

                <label className="label">تصویر شاخص</label>
                <CoverField
                  url={edit.cover_url}
                  alt={edit.cover_alt}
                  onUrl={(v) => set({ cover_url: v })}
                  onAlt={(v) => set({ cover_alt: v })}
                />

                <label className="label">چکیده</label>
                <textarea className="input mb-3 min-h-[70px]" value={edit.excerpt}
                          onChange={(e) => set({ excerpt: e.target.value })} />

                <label className="label">متن کامل</label>
                <div className="mb-4">
                  <Editor value={edit.body} onChange={(v) => set({ body: v })} />
                </div>

                <div className="mb-4 grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="label">وضعیت انتشار</label>
                    <select className="input" value={edit.status}
                            onChange={(e) => set({ status: e.target.value })}>
                      <option value="draft">پیش‌نویس</option>
                      <option value="published">منتشر شود</option>
                      <option value="scheduled">زمان‌بندی‌شده</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">
                      {edit.status === 'scheduled' ? 'تاریخ و ساعت انتشار (الزامی)' : 'تاریخ انتشار'}
                    </label>
                    <input type="datetime-local" className="input num" dir="ltr"
                           value={edit.published_at}
                           onChange={(e) => set({ published_at: e.target.value })} />
                  </div>
                </div>

                <label className="mb-4 flex items-center gap-2 text-xs text-slate-300">
                  <input type="checkbox" checked={edit.featured}
                         onChange={(e) => set({ featured: e.target.checked })} />
                  نمایش در بخش «مطالب ویژه» صفحه وبلاگ
                </label>
              </>
            )}

            {tab === 'seo' && (
              <>
                <div className="mb-4">
                  <div className="mb-2 flex items-center gap-2 text-xs text-slate-400">
                    <Search className="h-3.5 w-3.5" /> پیش‌نمایش نتیجه گوگل
                  </div>
                  <Serp seo={preview?.seo} slug={edit.slug || '—'} />
                </div>

                <label className="label">
                  عنوان صفحه (title) —{' '}
                  <span className={`num ${counterClass(titleLen, 25, 60)}`}>{fa(titleLen)} نویسه</span>
                </label>
                <input className="input mb-1" value={edit.meta_title} placeholder={edit.title}
                       onChange={(e) => set({ meta_title: e.target.value })} />
                <p className="mb-3 text-[11px] text-slate-500">
                  خالی بماند، از تیتر مقاله استفاده می‌شود. بهترین طول ۲۵ تا ۶۰ نویسه است.
                </p>

                <label className="label">
                  توضیح متا —{' '}
                  <span className={`num ${counterClass(descLen, 70, 160)}`}>{fa(descLen)} نویسه</span>
                </label>
                <textarea className="input mb-1 min-h-[80px]" value={edit.meta_description}
                          placeholder={edit.excerpt}
                          onChange={(e) => set({ meta_description: e.target.value })} />
                <p className="mb-3 text-[11px] text-slate-500">
                  همان متنی که زیر عنوان در نتایج گوگل دیده می‌شود؛ ۷۰ تا ۱۶۰ نویسه بنویسید.
                </p>

                <label className="label">کلیدواژه‌ها (اولین مورد، کلیدواژه اصلی در نظر گرفته می‌شود)</label>
                <input className="input mb-3" value={edit.keywords}
                       placeholder="خرید تتر، قیمت تتر"
                       onChange={(e) => set({ keywords: e.target.value })} />

                <label className="label">نشانی canonical (فقط اگر نسخه اصلی مطلب جای دیگری است)</label>
                <input className="input num mb-3" dir="ltr" value={edit.canonical}
                       onChange={(e) => set({ canonical: e.target.value })} />

                <label className="mb-4 flex items-center gap-2 text-xs text-slate-300">
                  <input type="checkbox" checked={edit.noindex}
                         onChange={(e) => set({ noindex: e.target.checked })} />
                  از نتایج جستجو حذف شود (noindex)
                </label>

                <Audit audit={preview?.audit} />
              </>
            )}

            {tab === 'faq' && <FaqEditor value={edit.faq} onChange={(v) => set({ faq: v })} />}

            <button onClick={save} disabled={!edit.title || saving}
                    className="btn-primary mt-5 w-full !py-3">
              <Save className="h-4 w-4" /> {saving ? 'در حال ذخیره…' : 'ذخیره مطلب'}
            </button>
          </>
        )}
      </Drawer>
    </>
  )
}
